import useIsMobile from '../hooks/useIsMobile';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, ChevronRight, Layout, LogOut, Sparkles, Settings2 } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import PlanSettingsModal from './PlanSettingsModal';
import { API, useAuth } from '../App';

const PLAN_COLORS = [
  '#6366f1','#8b5cf6','#06b6d4','#10b981',
  '#f59e0b','#ef4444','#ec4899','#14b8a6',
];
const DEPTH_DOTS = ['#6366f1','#8b5cf6','#06b6d4','#10b981'];

function timeAgo(ts) {
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function PlansView({ token, onOpenPlan, themePref, onThemeChange }) {
  const isMobile = useIsMobile();
  const { onLogout } = useAuth();
  const [plans, setPlans]       = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [inviteError, setInviteError] = useState('');
  const [loading, setLoading]   = useState(true);
  const [showNew, setShowNew]   = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc]   = useState('');
  const [newColor, setNewColor] = useState(PLAN_COLORS[0]);
  const [deleting, setDeleting] = useState(null);
  const [settingsPlan, setSettingsPlan] = useState(null);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  const jsonHeaders = token ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } : { 'Content-Type': 'application/json' };

  useEffect(() => { loadPlans(); }, []);

  async function loadPlans() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/plans`, { headers: authHeaders });
      if (r.status === 401) {
        // Token expired — kick back to login
        if (typeof onLogout === 'function') onLogout();
        return;
      }
      const data = await r.json();
      setPlans(Array.isArray(data) ? data : []);
      try {
        const pending = await fetch(`${API}/api/plan-invitations`, { headers: authHeaders });
        setInvitations(pending.ok ? await pending.json() : []);
      } catch { setInvitations([]); }
    } catch (err) {
      console.error('Failed to load plans:', err);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }

  async function createPlan() {
    if (!newTitle.trim()) return;
    const r = await fetch(`${API}/api/plans`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ title: newTitle, description: newDesc, color: newColor }),
    });
    const plan = await r.json();
    setShowNew(false); setNewTitle(''); setNewDesc(''); setNewColor(PLAN_COLORS[0]);
    onOpenPlan(plan);
  }

  async function deletePlan(id, e) {
    e.stopPropagation();
    if (deleting === id) {
      await fetch(`${API}/api/plans/${id}`, { method: 'DELETE', headers: authHeaders });
      setPlans(p => p.filter(x => x.id !== id));
      setDeleting(null);
    } else {
      setDeleting(id);
      setTimeout(() => setDeleting(null), 2500);
    }
  }

  async function answerInvitation(id, action) {
    setInviteError('');
    try {
      const response = await fetch(`${API}/api/plan-invitations/${id}/${action}`, { method: 'POST', headers: authHeaders });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Could not update invitation');
      await loadPlans();
    } catch (e) { setInviteError(e.message); }
  }

  const orderedPlans = [...plans.filter(p => p.role !== 'editor'),
    ...(plans.some(p => p.role === 'editor') ? [{ id: '__shared_section', section: true }] : []),
    ...plans.filter(p => p.role === 'editor')];

  return (
    <div style={{
      width: '100%', height: '100vh', overflowY: 'auto',
      background: 'var(--bg)',
      padding: '40px 44px 80px',
    }}>
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} style={{ marginBottom: 44 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 11,
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Layout size={18} color="white" />
            </div>
            <div>
              <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary)', letterSpacing: '-0.4px', lineHeight: 1 }}>
                IdeaCanvas
              </h1>
              <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>
                Your zoomable thinking space
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ThemeToggle value={themePref} onChange={onThemeChange} />
            <LogoutButton />
          </div>
        </div>
      </motion.div>

      {invitations.length > 0 && <section style={{ marginBottom: 28 }}>
        <h2 style={{ color: 'var(--text-primary)', fontSize: 16, marginBottom: 10 }}>Invitations</h2>
        {inviteError && <p role="alert" style={{ color: '#ef4444', fontSize: 12 }}>{inviteError}</p>}
        {invitations.map(invite => <div key={invite.id} style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ color: 'var(--text-primary)', fontSize: 13 }}><strong>{invite.title}</strong> · invited by {invite.owner_email}</span>
          <span style={{ display: 'flex', gap: 8 }}><button onClick={() => answerInvitation(invite.id, 'decline')} style={{ ...cancelBtn, flex: 'none', padding: '6px 10px' }}>Decline</button><button onClick={() => answerInvitation(invite.id, 'accept')} style={{ ...cancelBtn, flex: 'none', padding: '6px 10px', background: '#6366f1', color: '#fff' }}>Join plan</button></span>
        </div>)}
      </section>}

      {/* Grid */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
          {[1,2,3].map(i => <div key={i} className="skeleton" style={{ height: 152, borderRadius: 14 }} />)}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
          <AnimatePresence>
            {orderedPlans.map((plan, i) => plan.section ? <h2 key={plan.id} style={{ gridColumn: '1 / -1', color: 'var(--text-primary)', fontSize: 16, marginTop: 12 }}>Shared with me</h2> : (
              <motion.div key={plan.id}
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9 }} transition={{ delay: i * 0.04 }}
                whileHover={{ y: -3, scale: 1.01 }} whileTap={{ scale: 0.98 }}
                onClick={() => onOpenPlan(plan)}
                style={{
                  background: 'var(--card-bg)',
                  border: '1px solid var(--border)',
                  borderRadius: 14, padding: '20px 20px 16px',
                  cursor: 'pointer', position: 'relative', overflow: 'hidden',
                  boxShadow: 'var(--card-shadow)',
                  transition: 'border-color 0.15s',
                }}
              >
                <div style={{
                  position: 'absolute', top: 0, left: 0, right: 0, height: 3,
                  background: plan.color, borderRadius: '14px 14px 0 0',
                }} />
                <div style={{
                  position: 'absolute', top: -32, right: -32,
                  width: 100, height: 100, borderRadius: '50%',
                  background: plan.color, opacity: 0.06, pointerEvents: 'none',
                }} />

                <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
                  {DEPTH_DOTS.map((c, idx) => (
                    <div key={idx} style={{ width: 7, height: 7, borderRadius: '50%', background: c, opacity: 0.65 }} />
                  ))}
                </div>

                <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 5 }}>
                  {plan.title}
                </h3>
                {plan.role === 'editor' && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Editor · {plan.owner_email}</div>}
                {plan.description && (
                  <p style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55, marginBottom: 12 }}>
                    {plan.description}
                  </p>
                )}

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 }}>
                  <div style={{ fontSize: 11.5, color: 'var(--text-faint)', display: 'flex', gap: 8 }}>
                    <span>{plan.rootBoxCount} boxes</span>
                    <span>·</span>
                    <span>{timeAgo(plan.updated_at)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {plan.role !== 'editor' && <button aria-label={`Settings for ${plan.title}`} title="Plan settings" onClick={e => { e.stopPropagation(); setSettingsPlan(plan); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 5, color: 'var(--text-faint)', display: 'flex' }}><Settings2 size={14} /></button>}
                    {plan.role !== 'editor' && <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                      onClick={(e) => deletePlan(plan.id, e)}
                      style={{
                        background: deleting === plan.id ? 'rgba(239,68,68,0.12)' : 'transparent',
                        border: 'none', cursor: 'pointer', padding: 5, borderRadius: 7,
                        color: deleting === plan.id ? '#ef4444' : 'var(--text-faint)', display: 'flex',
                      }}>
                      <Trash2 size={13} />
                    </motion.button>}
                    <ChevronRight size={15} color={plan.color} />
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* New plan */}
          {plans.some(plan => plan.role === 'editor') && <h2 style={{ gridColumn: '1 / -1', color: 'var(--text-primary)', fontSize: 16, marginTop: 12 }}>Create your own plan</h2>}
          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: plans.length * 0.04 }}
            onClick={() => setShowNew(true)}
            whileHover={{ y: -3, scale: 1.01 }} whileTap={{ scale: 0.98 }}
            style={{
              background: 'transparent',
              border: '1px dashed rgba(99,102,241,0.35)',
              borderRadius: 14, padding: 20, cursor: 'pointer',
              display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              gap: 8, minHeight: 152, color: '#6366f1',
            }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(99,102,241,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Plus size={18} />
            </div>
            <span style={{ fontSize: 13, fontWeight: 600 }}>New Plan</span>
          </motion.div>
        </div>
      )}

      {/* New plan modal */}
      <AnimatePresence>
        {showNew && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 100, backdropFilter: 'blur(5px)',
            }}
            onClick={() => setShowNew(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92 }}
              onClick={e => e.stopPropagation()}
              style={{
                background: 'var(--bg-3)',
                border: '1px solid var(--border)',
                borderRadius: 18, padding: 28, width: 420, maxWidth: '90vw',
                boxShadow: 'var(--card-shadow-hover)',
              }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 22 }}>
                <Sparkles size={16} color="#6366f1" />
                <h2 style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>New Plan</h2>
              </div>

              <label style={lbl}>TITLE</label>
              <input autoFocus value={newTitle} onChange={e => setNewTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createPlan()}
                placeholder="e.g. NoseMD Launch Strategy"
                style={{ ...inp, marginBottom: 14 }} />

              <label style={lbl}>DESCRIPTION (optional)</label>
              <textarea value={newDesc} onChange={e => setNewDesc(e.target.value)}
                placeholder="A brief description..." rows={2}
                style={{ ...inp, resize: 'none', marginBottom: 18 }} />

              <label style={lbl}>COLOR</label>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 22 }}>
                {PLAN_COLORS.map(c => (
                  <motion.button key={c} whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}
                    onClick={() => setNewColor(c)}
                    style={{
                      width: 26, height: 26, borderRadius: '50%', background: c,
                      border: newColor === c ? '2px solid var(--text-primary)' : '2px solid transparent',
                      cursor: 'pointer', outline: 'none',
                    }} />
                ))}
              </div>

              <div style={{ display: 'flex', gap: 9 }}>
                <button onClick={() => setShowNew(false)} style={{ ...cancelBtn }}>Cancel</button>
                <button onClick={createPlan} disabled={!newTitle.trim()}
                  style={{
                    flex: 2, padding: '10px 0', borderRadius: 9, border: 'none',
                    background: newTitle.trim() ? newColor : 'var(--bg-4)',
                    color: newTitle.trim() ? '#fff' : 'var(--text-muted)',
                    cursor: newTitle.trim() ? 'pointer' : 'not-allowed',
                    fontSize: 14, fontWeight: 600, fontFamily: 'inherit', transition: 'all 0.2s',
                  }}>
                  Create Plan →
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {settingsPlan && <PlanSettingsModal plan={settingsPlan} token={token} onPlanUpdated={updated => { setPlans(current => current.map(p => p.id === updated.id ? { ...p, ...updated } : p)); setSettingsPlan(current => ({ ...current, ...updated })); }} onClose={() => setSettingsPlan(null)} />}
    </div>
  );
}

function LogoutButton() {
  const { onLogout } = useAuth();
  return (
    <button onClick={onLogout} title="Sign out" style={{
      background: 'var(--bg-4)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 5,
      color: 'var(--text-muted)', fontSize: 12, fontWeight: 500, fontFamily: 'inherit',
    }}>
      <LogOut size={13} /> Sign out
    </button>
  );
}

const lbl = { fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 6, fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' };
const inp = { width: '100%', background: 'var(--bg-4)', border: '1px solid var(--border)', borderRadius: 9, padding: '9px 13px', color: 'var(--text-primary)', fontSize: 14, outline: 'none', fontFamily: 'Inter, sans-serif', lineHeight: 1.5 };
const cancelBtn = { flex: 1, padding: '10px 0', borderRadius: 9, background: 'var(--bg-4)', border: '1px solid var(--border)', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14, fontWeight: 500, fontFamily: 'inherit' };
